/**
 * update_flow tool - Update an existing Power Automate flow
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import type { Flow, UpdateFlowRequest, FlowDefinition, ConnectionReference } from "../api/types.js";
import { validateFlow } from "../validator/index.js";
import { logger } from "../utils/logger.js";
import { preprocessFlowActions } from "../utils/expression-escape.js";
import { ValidationError } from "../utils/errors.js";
import { extractArmErrorMessage, ERROR_DETAIL_LIMIT } from "../utils/format.js";
import {
  describeConnectionAuthFailure,
  ensureConnectionParameters,
  environmentLabelFor,
  resolveLogicalConnectionNames,
} from "./connection-binding.js";

/**
 * Prototype-pollution reserved keys. LLM-supplied merge/patch input must never set
 * these — a `__proto__` key silently changes an object's prototype instead of setting
 * a property (wrong even when the result is later JSON-serialized). Rejected loudly in
 * both merge paths, path segments included.
 */
const RESERVED_KEYS = new Set(["__proto__", "constructor", "prototype"]);

const triggerSchema = z.object({
  type: z.string(),
  kind: z.string().optional(),
  inputs: z.record(z.string(), z.unknown()).optional(),
  recurrence: z
    .object({
      frequency: z.enum(["Second", "Minute", "Hour", "Day", "Week", "Month"]),
      interval: z.number().min(1),
      startTime: z.string().optional(),
      timeZone: z.string().optional(),
    })
    .optional(),
}).passthrough(); // Preserve extra trigger properties: splitOn, conditions, metadata, etc.

const actionSchema = z.object({
  type: z.string(),
  inputs: z.unknown().optional(),
  runAfter: z.record(z.string(), z.array(z.enum(["Succeeded", "Failed", "Skipped", "TimedOut"]))).optional(),
}).passthrough(); // Preserve nested properties: actions, foreach, expression, else, cases (Foreach/Until/If/Switch/Scope)

const connectionRefSchema = z.object({
  connectionName: z.string(),
  id: z.string(),
  source: z.enum(["Invoker", "Embedded"]).optional().default("Embedded"),
  tier: z.enum(["Standard", "Premium", "NotSpecified"]).optional().default("NotSpecified"),
});

export const updateFlowInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID) to update"),
  displayName: z.string().optional().describe("New display name for the flow"),
  description: z
    .string()
    .optional()
    .describe("New description (set to empty string to clear)"),
  trigger: triggerSchema.optional().describe("New trigger definition (replaces existing)"),
  triggerName: z.string().optional().describe("Name for the trigger"),
  actions: z
    .record(z.string(), actionSchema)
    .optional()
    .describe(
      "Actions. By default REPLACES all existing actions (you must include every action you want kept). " +
        "When mergeActions=true, only the named actions are added/updated; everything else stays. " +
        "Use patchActions for surgical edits to a single nested branch (smallest payload).",
    ),
  mergeActions: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      "When true, deep-merge the provided 'actions' onto the current flow's actions instead of replacing wholesale. " +
        "Recurses into nested actions/cases/else/foreach. Lets you patch a sub-tree without resending the entire flow.",
    ),
  patchActions: z
    .record(z.string(), actionSchema.nullable())
    .optional()
    .describe(
      "Path-based patch map. Keys are slash-separated paths into the action tree, e.g. " +
        "'If_Recognized_Form/cases/Default/actions/Compose'. Set value to null to delete a node. " +
        "Smallest payload — only the changed branches travel. Applied AFTER mergeActions.",
    ),
  connectionReferences: z
    .record(z.string(), connectionRefSchema)
    .optional()
    .describe("New connection references (replaces existing)"),
  environment: z.string().optional().describe("Environment name or GUID"),
  validateBeforeUpdate: z.boolean().optional().default(true).describe("Validate before updating"),
});

export type UpdateFlowInput = z.infer<typeof updateFlowInputSchema>;

export const updateFlowTool = {
  name: "update_flow",
  description:
    "Update an existing Power Automate flow. Three update modes: " +
    "(1) full replace — pass 'actions' to replace all top-level actions (default; you must include nested actions you want kept), " +
    "(2) merge — set mergeActions=true to deep-merge only the actions you provide, leaving the rest intact, " +
    "(3) patch — use patchActions with path keys (e.g. 'If/cases/Default/actions/Compose') for surgical edits with the smallest payload. " +
    "Use get_flow with format='json' first to see the full nested action tree.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID) to update",
      },
      displayName: {
        type: "string",
        description: "New display name for the flow",
      },
      description: {
        type: "string",
        description: "New description for the flow (empty string clears)",
      },
      trigger: {
        type: "object",
        description: "New trigger definition (replaces existing)",
      },
      triggerName: {
        type: "string",
        description: "Name for the trigger",
      },
      actions: {
        type: "object",
        description:
          "Actions. By default replaces all existing top-level actions. With mergeActions=true, deep-merges instead.",
      },
      mergeActions: {
        type: "boolean",
        description:
          "When true, deep-merge 'actions' onto current flow instead of replacing. Recurses into nested actions/cases/else/foreach.",
        default: false,
      },
      patchActions: {
        type: "object",
        description:
          "Path-based patch map. Keys: slash-separated paths like 'If/cases/Default/actions/Compose'. Value=null deletes the node. Smallest payload.",
      },
      connectionReferences: {
        type: "object",
        description: "New connection references (replaces existing)",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID",
      },
      validateBeforeUpdate: {
        type: "boolean",
        description: "Validate before updating (default: true)",
      },
    },
    required: ["flowId"],
  },
};

/**
 * Deep-merge `patch` onto `base`, recursing into objects. Arrays are replaced wholesale.
 * Returns a new object — does not mutate inputs.
 */
export function deepMerge(
  base: Record<string, unknown>,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if (RESERVED_KEYS.has(key)) {
      throw new ValidationError(`Disallowed key in flow update: '${key}'`);
    }
    const baseVal = out[key];
    if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      baseVal !== null &&
      typeof baseVal === "object" &&
      !Array.isArray(baseVal)
    ) {
      out[key] = deepMerge(
        baseVal as Record<string, unknown>,
        value as Record<string, unknown>,
      );
    } else {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Apply a single path patch. Path is slash-separated. Value=null deletes the node.
 * Returns a new actions object.
 */
export function applyPathPatch(
  actions: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return actions;
  const bad = segments.find((s) => RESERVED_KEYS.has(s));
  if (bad) {
    throw new ValidationError(`Disallowed key in patch path: '${bad}'`);
  }

  const result: Record<string, unknown> = structuredClone(actions);
  let cursor: Record<string, unknown> = result;

  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i]!;
    const next = cursor[seg];
    if (next === null || typeof next !== "object" || Array.isArray(next)) {
      cursor[seg] = {};
    }
    cursor = cursor[seg] as Record<string, unknown>;
  }

  const lastSeg = segments[segments.length - 1]!;
  if (value === null) {
    delete cursor[lastSeg];
  } else {
    cursor[lastSeg] = value;
  }
  return result;
}

/**
 * The single most common update_flow mistake: a patchActions path or a
 * mergeActions/actions key that is NOT rooted at a top-level action. Because
 * `applyPathPatch`/`deepMerge` create missing segments, such a key is silently
 * grafted on as a new — and invalid, type-less — top-level action, which then
 * fails validation with an opaque "Action must have a type". Name the fix once
 * so the caller isn't left guessing.
 */
export const UPDATE_PATH_ROOTING_TIP =
  "Tip: 'patchActions' paths and 'actions'/'mergeActions' keys must start at a TOP-LEVEL action and " +
  'descend through \'actions\'/\'cases\'/\'else\' (e.g. patchActions {"Scope/actions/Loop/actions/Child": {…}}). ' +
  "A key or path that does not resolve to an existing action is grafted on as a new, invalid top-level action. " +
  "Run get_flow with format='json' to see the exact action tree.";

/**
 * Turn a Zod parse failure of update/preview input into an actionable message:
 * name each bad field, spell out the common "action is missing its required
 * `type`" case, and — when the mistake is action-shaped — point at the
 * top-level-rooting rule. Exported for regression testing.
 */
export function describeUpdateInputError(err: z.ZodError): string {
  let actionShaped = false;
  const shown = err.issues.slice(0, 5);
  const bullets = shown.map((iss) => {
    const path = iss.path.map(String);
    const joined = path.join(".");
    const last = path[path.length - 1];
    if (path.includes("actions") || path.includes("patchActions")) actionShaped = true;
    if (last === "type" && iss.code === "invalid_type") {
      actionShaped = true;
      const owner = path.slice(0, -1).join(".") || "(action)";
      return `  • ${owner}: missing required 'type' — every action must include its "type" (e.g. "Compose", "If", "Foreach", "Scope", "Http").`;
    }
    return `  • ${joined || "(root)"}: ${iss.message}`;
  });
  const lines = ["Invalid update input:", ...bullets];
  if (err.issues.length > shown.length) {
    lines.push(`  • …and ${err.issues.length - shown.length} more`);
  }
  if (actionShaped) lines.push("", UPDATE_PATH_ROOTING_TIP);
  return lines.join("\n");
}

/**
 * The resolved shape of an update before it is applied. `preview_update`
 * renders this; `update_flow` PATCHes it — so both agree by construction.
 */
export interface ResolvedFlowUpdate {
  ok: true;
  currentFlow: Flow;
  etag?: string;
  newDisplayName: string;
  newDefinition: FlowDefinition;
  newConnRefs: Record<string, ConnectionReference>;
  /** Notes about logical (source "Invoker") connection names that were
   * resolved to real connections before the save. */
  connectionResolutionNotes: string[];
}

export type FlowUpdateResolution = ResolvedFlowUpdate | { ok: false; message: string };

/**
 * Read the flow and compute the definition/connection references an update
 * would produce, applying merge/patch/replace semantics and optional
 * validation. Performs no writes.
 */
export async function resolveFlowUpdate(
  api: FlowManagementApi,
  parsed: UpdateFlowInput
): Promise<FlowUpdateResolution> {

  logger.info(
    {
      flowId: parsed.flowId,
      mergeActions: parsed.mergeActions,
      hasPatch: !!parsed.patchActions,
    },
    "Executing update_flow",
  );

  // Get the current flow to merge with updates. The ETag (when the service provides
  // one) makes the read-modify-write below conditional: if someone else edits the
  // flow between our read and our PATCH, the service can reject with 412 instead of
  // silently clobbering their change (#8).
  const { flow: currentFlow, etag } = await api.getFlowWithEtag(parsed.flowId, parsed.environment);
  const currentDef = currentFlow.properties.definition;
  const currentConnRefs = currentFlow.properties.connectionReferences;

  // Determine what to update
  const newDisplayName = parsed.displayName ?? currentFlow.properties.displayName;
  // description: explicit empty string clears; undefined leaves as-is. The flows
  // API rejects properties.description on write ("Invalid JSON at path
  // 'properties.description'") — it lives INSIDE the workflow definition
  // (verified live 2026-07-05); reads may surface it at either location.
  const newDescription =
    parsed.description !== undefined
      ? parsed.description
      : (currentFlow.properties.description ?? currentDef.description);

  // Build new definition
  let newDefinition: FlowDefinition;

  // Determine the resolved actions object using mergeActions / patchActions / replace semantics.
  let resolvedActions: Record<string, unknown> | undefined;
  if (parsed.actions || parsed.patchActions) {
    if (parsed.mergeActions && parsed.actions) {
      // Deep-merge supplied actions onto current
      resolvedActions = deepMerge(
        currentDef.actions,
        parsed.actions,
      );
    } else if (parsed.actions) {
      // Replace
      resolvedActions = parsed.actions;
    } else {
      // Only patchActions provided — start from current actions
      resolvedActions = structuredClone(currentDef.actions);
    }

    // Apply path-based patches on top
    if (parsed.patchActions) {
      for (const [path, value] of Object.entries(parsed.patchActions)) {
        resolvedActions = applyPathPatch(resolvedActions, path, value);
      }
    }
  }

  if (parsed.trigger || resolvedActions) {
    // If either trigger or actions are provided, we need to rebuild the definition
    const triggerName = parsed.triggerName ?? Object.keys(currentDef.triggers)[0] ?? "manual";

    // Preprocess actions to fix payload objects that contain @{} expressions.
    const processedActions = resolvedActions
      ? preprocessFlowActions(resolvedActions)
      : currentDef.actions;

    newDefinition = {
      $schema: currentDef.$schema,
      contentVersion: currentDef.contentVersion,
      ...(newDescription ? { description: newDescription } : {}),
      triggers: parsed.trigger
        ? { [triggerName]: parsed.trigger }
        : currentDef.triggers,
      actions: processedActions as FlowDefinition["actions"],
    };

    if (currentDef.parameters) {
      newDefinition.parameters = currentDef.parameters;
    }
  } else {
    // Copy so a description change (set or clear) never mutates the fetched flow.
    newDefinition = { ...currentDef };
    if (newDescription) {
      newDefinition.description = newDescription;
    } else {
      delete newDefinition.description;
    }
  }

  // Build connection references
  let newConnRefs: Record<string, ConnectionReference>;

  const connectionResolutionNotes: string[] = [];

  if (parsed.connectionReferences) {
    newConnRefs = {};
    for (const [key, ref] of Object.entries(parsed.connectionReferences)) {
      newConnRefs[key] = {
        connectionName: ref.connectionName,
        id: ref.id,
        source: ref.source ?? "Embedded",
        tier: ref.tier ?? "NotSpecified",
      };
    }

    // A source "Invoker" reference carries a logical connectionName, not a
    // real connection id — resolve it against the environment's actual
    // connections so the API doesn't reject a connection that exists (#see
    // connection-binding.ts). Fails open when connections can't be listed.
    const resolution = await resolveLogicalConnectionNames(
      api,
      newConnRefs,
      parsed.environment,
      environmentLabelFor(api, parsed.environment),
    );
    if (resolution.missing.length > 0) {
      return { ok: false, message: `Cannot update flow: ${resolution.missing.join(" ")}` };
    }
    newConnRefs = resolution.refs;
    connectionResolutionNotes.push(...resolution.resolved);
  } else {
    newConnRefs = currentConnRefs;
  }

  // The service rejects any definition that binds connections without
  // declaring the `$connections` parameter ("workflow parameters
  // '$connections' are not valid"). A flow created without connector actions
  // never declared it, so the update that adds the FIRST connector action
  // must inject it — carrying over currentDef.parameters is not enough.
  ensureConnectionParameters(newDefinition, newConnRefs);

  // Validate if requested
  if (parsed.validateBeforeUpdate) {
    const validation = validateFlow(newDefinition, newConnRefs);

    const schemaErrors = validation.schemaValidation.errors;
    const exprErrors = validation.expressionValidation.errors.filter((e) => e.severity === "error");
    let connErrors = validation.connectionValidation.errors.filter((e) => e.severity === "error");

    // Connection references carried over UNCHANGED from the live flow are
    // exempt from per-reference structure checks (connectionName/id/source):
    // flows created in the Power Automate designer store references in a
    // modern shape without those builder-schema fields, and re-validating
    // refs this update doesn't touch false-blocked metadata-only updates
    // (e.g. a description change) with "Connection reference missing
    // connectionName". Missing/unused-connection checks still apply — they
    // key off reference names and catch actions that need a connection the
    // carried-over set doesn't provide.
    if (!parsed.connectionReferences) {
      connErrors = connErrors.filter(
        (e) => !/\.(connectionName|id|source)$/.test(e.path)
      );
    }

    if (schemaErrors.length > 0 || exprErrors.length > 0 || connErrors.length > 0) {
      const lines: string[] = ["Cannot update flow - validation failed:"];

      // Name the offending path on every bullet. The validator already knows
      // exactly which action/field is wrong (e.g. "actions.Loop_Issues.type"),
      // but dropping the path reduced "Action must have a type" to a nameless
      // riddle — the caller couldn't tell WHICH action, or that it had been
      // grafted at the wrong depth by a mis-rooted patch/merge.
      const bullet = (e: { path?: string; message: string }): string =>
        `  • ${e.path ? `${e.path}: ` : ""}${e.message}`;

      if (schemaErrors.length > 0) {
        lines.push("", "Schema issues:");
        schemaErrors.slice(0, 3).forEach((e) => lines.push(bullet(e)));
        if (schemaErrors.length > 3) lines.push(`  ... and ${schemaErrors.length - 3} more`);
      }

      if (exprErrors.length > 0) {
        lines.push("", "Expression issues:");
        exprErrors.slice(0, 3).forEach((e) => lines.push(bullet(e)));
      }

      if (connErrors.length > 0) {
        lines.push("", "Connection issues:");
        connErrors.slice(0, 3).forEach((e) => lines.push(bullet(e)));
      }

      // A type-less action / dangling runAfter / missing foreach is the
      // signature of a patch or merge that landed at the wrong depth. Point at
      // the rooting rule once, at the end, so the fix is obvious.
      if (
        schemaErrors.some((e) =>
          /(must have a type|References non-existent action|must have a foreach)/i.test(e.message),
        )
      ) {
        lines.push("", UPDATE_PATH_ROOTING_TIP);
      }

      return { ok: false, message: lines.join("\n") };
    }
  }

  return {
    ok: true,
    currentFlow,
    etag,
    newDisplayName,
    newDefinition,
    newConnRefs,
    connectionResolutionNotes,
  };
}

export async function handleUpdateFlow(
  api: FlowManagementApi,
  input: unknown
): Promise<string> {
  let parsed: UpdateFlowInput;
  try {
    parsed = updateFlowInputSchema.parse(input);
  } catch (err) {
    // Surface a named, actionable message (which field, and the rooting rule)
    // instead of the raw Zod "expected string, received undefined" — the whole
    // barrier to a correct edit was not knowing WHAT was malformed. No API call
    // has happened yet, so returning the message is safe.
    if (err instanceof z.ZodError) return describeUpdateInputError(err);
    throw err;
  }
  const resolved = await resolveFlowUpdate(api, parsed);
  if (!resolved.ok) return resolved.message;
  const { etag, newDisplayName, newDefinition, newConnRefs, connectionResolutionNotes } = resolved;

  // Build update request
  const updateRequest: UpdateFlowRequest = {
    properties: {
      displayName: newDisplayName,
      definition: newDefinition,
      connectionReferences: newConnRefs,
    },
  };

  try {
    const updatedFlow = await api.updateFlow(parsed.flowId, updateRequest, parsed.environment, {
      ifMatch: etag,
    });
    const resolutionSuffix =
      connectionResolutionNotes.length > 0
        ? `\nConnection references resolved: ${connectionResolutionNotes.join("; ")}`
        : "";
    return `Updated "${updatedFlow.properties.displayName}" (${updatedFlow.name}) - ${updatedFlow.properties.state}${resolutionSuffix}`;
  } catch (error) {
    // Extract meaningful error message from API response
    const msg = error instanceof Error ? error.message : String(error);
    let details = "";
    let extracted: string | undefined;

    if (error && typeof error === "object" && "details" in error) {
      const errDetails = (error).details;
      // Parse the error body properly (nested/stringified {error: {message}}
      // forms) — the old regex clipped messages at the first escaped quote,
      // hiding e.g. the allowed enum values the user needs to fix the flow.
      extracted = extractArmErrorMessage(errDetails);
      if (extracted) {
        details = extracted;
      } else if (typeof errDetails === "string") {
        details = errDetails;
      } else if (errDetails && typeof errDetails === "object") {
        details = JSON.stringify(errDetails).slice(0, ERROR_DETAIL_LIMIT);
      }
    }

    // Parse common error patterns
    if (msg.includes("InvalidOpenApiFlow") || msg.includes("DynamicOperationRequestClientFailure")) {
      // The extractor already unwrapped any nested "Error response: {...}"
      // message, so surface it directly.
      if (extracted) {
        return `Flow update failed: ${extracted.slice(0, ERROR_DETAIL_LIMIT)}`;
      }

      // Check for common issues
      if (details.includes("GetTable") || details.includes("BadRequest")) {
        return `Flow update failed: Invalid Excel/SharePoint parameters. Check drive ID, file ID, and table name are correct.`;
      }

      return `Flow update failed: Invalid flow definition. ${details.slice(0, ERROR_DETAIL_LIMIT)}`;
    }

    if (msg.includes("ConnectionAuthorizationFailed")) {
      // Name the environment that was checked and what actually exists for
      // this connector there — "create the connection" is misleading advice
      // when a healthy connection exists in a different environment.
      const enriched = await describeConnectionAuthFailure(
        api,
        `${msg} ${details}`,
        parsed.environment,
        environmentLabelFor(api, parsed.environment),
      );
      if (enriched) return `Flow update failed: ${enriched}`;
      const connMatch = msg.match(/apis\/([^/'"]+)/);
      const connName = connMatch ? connMatch[1]!.replace("shared_", "") : "a connector";
      return `Flow update failed: Missing ${connName} connection. Create it at make.powerautomate.com > Data > Connections.`;
    }

    return `Flow update failed: ${msg.slice(0, ERROR_DETAIL_LIMIT)}${details ? ` - ${details.slice(0, ERROR_DETAIL_LIMIT)}` : ""}`;
  }
}
